<script setup>
import { computed, onMounted, reactive, ref, useSlots, watch } from 'vue'
import SingleNote from './_singleNote.vue'
import { useNotificationStore } from '@/stores/NotificationStore.js'

const props = defineProps({
    alignment: {
        type: String,
        default: ''
    },
    lead: {
        type: Object,
        required: true
    },
    notes: {
        type: [Array, Object],
        default: () => []
    }
})

const notificationStore = useNotificationStore()
const slots = useSlots()

const isCreatingNote = ref(false)
const open = ref(false)
const working = ref(false)
const pendingDeleteNoteId = ref(null)
const loaded = ref(false)

const noteEditor = ref(null)
const noteFile = ref(null)
const editNoteFile = ref(null)

const errors = reactive({
    content: null
})

const creatingNote = reactive({
    note: '',
    attachment: {},
    note_date: ''
})

const currentlyEditingNote = reactive({
    id: '',
    note: '',
    note_date: '',
    attachment: null,
    attachment_url: ''
})

const localnotes = ref([])
const notification = reactive({
    type: '',
    message: ''
})

onMounted(() => {
    loadNotes()
})

const hasHeaderSlot = computed(() => !!(slots['header-left'] && slots['header-right']))
const hasImageSlot = computed(() => !!slots.image)
const hasBodySlot = computed(() => !!slots.body)
const alignmentClass = computed(() => (props.alignment === 'center' ? 'text-center' : ''))

const closeSlideout = () => {
    open.value = false
    if (editNoteFile.value) {
        editNoteFile.value.value = null
    }
}

const closeNotification = () => {
    notification.message = ''
}

const abortDelete = () => {
    pendingDeleteNoteId.value = null
}

const openInSlideout = (note) => {
    abortDelete()
    Object.assign(currentlyEditingNote, {
        ...note,
        note_date: note.note_date || note.created_at,
        attachment: null
    })
    open.value = true
}

const resetCreateNoteForm = () => {
    creatingNote.note = ''
    creatingNote.attachment = {}
    creatingNote.note_date = ''
    errors.content = null
    noteEditor.value?.resetEditor?.()
    if (noteFile.value) {
        noteFile.value.value = null
    }
}

const handleEditFile = (e) => {
    const file = e.target.files?.[0] || null
    currentlyEditingNote.attachment = file
}

const handleFile = (e) => {
    const file = e.target.files?.[0] || {}
    creatingNote.attachment = file
}

const loadNotes = async () => {
    const res = await axios.get(route('crm.lead.notes', props.lead.id))
    localnotes.value = res.data.notes
    loaded.value = true
}

const createNote = async () => {
    if (creatingNote.note) {
        working.value = true
        const formData = new FormData()
        formData.append('attachment', creatingNote.attachment)
        formData.append('note', creatingNote.note)
        if (creatingNote.note_date) {
            formData.append('note_date', creatingNote.note_date)
        }

        const res = await axios.post(route('crm.leads.note.create', props.lead.id), formData)

        notificationStore.addNotification({
            type: res.data.notification.type,
            message: res.data.notification.message,
            dismissAfter: 2500
        })

        loadNotes()
        resetCreateNoteForm()
        working.value = false
        isCreatingNote.value = false
    } else {
        errors.content = _mft('crm:leadNoteCard.fieldMissing')
    }
}

const updateNote = async () => {
    working.value = true
    const formData = new FormData()
    formData.append('note', currentlyEditingNote.note)
    formData.append('note_date', currentlyEditingNote.note_date)

    if (currentlyEditingNote.attachment) {
        formData.append('file', currentlyEditingNote.attachment)
    }

    const res = await axios.post(route('crm.leads.note.update', currentlyEditingNote.id), formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
            'X-HTTP-Method-Override': 'PUT'
        }
    })

    notificationStore.addNotification({
        type: res.data.notification.type,
        message: res.data.notification.message,
        dismissAfter: 2500
    })

    if (res.data.notes) {
        localnotes.value = res.data.notes
    }
    working.value = false
    closeSlideout()
    await loadNotes()
}

const confirmDelete = async (noteId) => {
    working.value = true
    try {
        const res = await axios.delete(route('crm.leads.note.delete', noteId))

        notificationStore.addNotification({
            type: res.data.notification.type,
            message: res.data.notification.message,
            dismissAfter: 2500
        })

        currentlyEditingNote.id = null
        open.value = false
        loadNotes()
        closeSlideout()
    } catch (error) {
        notification.type = 'error'
        notification.message = error?.response?.data?.message || 'Failed to delete note'
    } finally {
        working.value = false
    }
}

defineExpose({
    openInSlideout,
    createNote,
    updateNote,
    handleEditFile,
    handleFile,
    resetCreateNoteForm,
    closeSlideout,
    closeNotification,
    abortDelete,
    confirmDelete,
    isCreatingNote,
    open,
    working,
    localnotes,
    notification,
    creatingNote,
    currentlyEditingNote,
    hasHeaderSlot,
    hasImageSlot,
    hasBodySlot,
    alignmentClass
})
</script>

<template>
    <div>
        <div class="mb-8">
            <mf-spinner-medium v-if="!loaded" class="w-12 h-6"></mf-spinner-medium>
            <div v-if="loaded">
                <list-basic>
                    <template #header>
                        <div class="flex justify-between items-center mb-2">
                            <h2>{{ _mft('crm:leadNoteCard.domainNamePlural') }}</h2>
                            <button-primary
                                @click="isCreatingNote = true"
                            >
                                {{ _mft('crm:leadNoteCard.newNote') }}
                            </button-primary>
                        </div>
                    </template>
                    <list-basic-item
                        v-if="localnotes.length === 0"
                        :compact="true"
                        margin-bottom-class="mb-2"
                    >
                        <div class="text-center text-gray-400 pt-3 pb-2">
                            {{ _mft('crm:leadNoteCard.noNotesAdded') }}
                        </div>
                    </list-basic-item>
                    <list-basic-item
                        v-for="(note, i) in localnotes"
                        v-bind:key="i"
                    >
                        <SingleNote
                            :note="note"
                            @open-in-slideout="openInSlideout"
                        ></SingleNote>
                    </list-basic-item>
                </list-basic>
            </div>

            <slideout-with-slots
                v-if="open"
                @close="open = false"
            >
                <template v-slot:title>
                    <h1>{{ _mft('crm:leadNoteCard.editNote') }}</h1>
                </template>

                <template v-slot:body>
                    <input-editor
                        label=""
                        :required="false"
                        :content="currentlyEditingNote.note"
                        name="note"
                        v-model:content="currentlyEditingNote.note"
                    />

                    <div class="mt-4">
                        <input-label
                            :label="_mft('crm:leadNoteCard.noteDate')"
                        />
                        <input
                            type="datetime-local"
                            v-model="currentlyEditingNote.note_date"
                            class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-myflowPurple-500 focus:border-myflowPurple-500"
                        />
                    </div>

                    <div class="mt-4">
                        <input-label
                            :label="_mft('crm:leadNoteCard.uploadFile')"
                        />
                        <div
                            v-if="currentlyEditingNote.attachment_url"
                            class="mb-2"
                        >
                            <p class="text-sm text-gray-600">{{ _mft('crm:leadNoteCard.currentFile') }}:</p>
                            <a
                                :href="currentlyEditingNote.attachment_url"
                                target="_blank"
                                class="text-sm mf-link"
                            >
                                <i class="mr-1 text-gray-500 fa-solid fa-paperclip"></i>
                                {{ _mft('crm:leadNoteCard.viewAttachment') }}
                            </a>
                        </div>
                        <input
                            class="w-full mt-1"
                            type="file"
                            ref="editNoteFile"
                            @change="handleEditFile"
                        />
                    </div>

                    <div class="flex justify-between items-center space-x-4 mt-6">
                        <button-with-confirmation
                            style-type="danger"
                            confirm-type="danger"
                            modal-class="absolute bottom-full mb-2 -left-2 p-2 bg-white rounded shadow-md text-black z-modal"
                            :confirm-text="_mft('shared:confirm.areYouSure')"
                            :confirm-button-label="_mft('shared:action.delete')"
                            :abort-button-label="_mft('shared:action.cancel')"
                            @confirmed="confirmDelete(currentlyEditingNote.id)"
                        >
                            <i class="fa-regular fa-trash"></i>
                        </button-with-confirmation>
                        <button-success
                            type="success"
                            :state="working"
                            v-on:click="updateNote"
                        >
                            {{ _mft('shared:action.update') }}
                        </button-success>
                    </div>

                </template>

            </slideout-with-slots>
        </div>
    </div>
    <slideout-with-slots
        v-if="isCreatingNote"
        @close="isCreatingNote = false"
    >
        <template #body>
            <form @submit.prevent="createNote">
                <input-editor
                    :label="_mft('crm:leadNoteCard.domainNameSingular')"
                    required="required"
                    :content="creatingNote.note"
                    name="note"
                    v-model:content="creatingNote.note"
                    ref="noteEditor"
                    autofocus
                    :error="errors.content"
                />

                <div class="mt-4">
                    <input-label
                        :label="_mft('crm:leadNoteCard.noteDate')"
                    />
                    <input
                        type="datetime-local"
                        v-model="creatingNote.note_date"
                        class="w-full px-3 py-2 mt-1 border border-gray-300 rounded-md focus:outline-none focus:ring-myflowPurple-500 focus:border-myflowPurple-500"
                    />
                </div>

                <div class="mt-4">
                    <input-label
                        :label="_mft('crm:leadNoteCard.uploadFile')"
                    />
                    <input
                        class="w-full mt-1"
                        type="file"
                        ref="noteFile"
                        @change="handleFile"
                    >
                </div>

                <div class="flex justify-end mt-4">
                    <input-button
                        :label="_mft('crm:leadNoteCard.saveNote')"
                        type="submit"
                        :busy="working"
                    />
                </div>
            </form>
        </template>
    </slideout-with-slots>
</template>
<style scoped>
.note-content a {
    text-decoration: underline;
}
</style>
